import type { CityConfig } from "@/pages/city-template/types";

export const newbergConfig: CityConfig = {
  city: "Newberg",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "newberg",
  route: "/cities/newberg",
  title: "Emergency Passport Newberg, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Newberg, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Yamhill County wine country and George Fox University travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Newberg, Oregon",
  heroSubtitle: "Heading to wine country in France from PDX? Passport expired before your George Fox University study abroad semester? Fast Passport Center helps Newberg and Yamhill County travelers get passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Newberg%20Oregon%20wine%20country%20landscape%20with%20rolling%20vineyard%20hills%20in%20Yamhill%20County%20at%20golden%20hour%2C%20lush%20green%20grapevines%20stretching%20across%20gentle%20slopes%2C%20Chehalem%20Mountains%20in%20the%20background%2C%20warm%20harvest%20light%20casting%20long%20shadows%20across%20the%20vines%2C%20pastoral%20Pacific%20Northwest%20atmosphere%2C%20photorealistic%20landscape%20photography%2C%20no%20text%20or%20signs%20visible&width=1800&height=700&seq=newberg-or-hero-01&orientation=landscape",
  heroImageAlt: "Newberg Oregon wine country landscape with Yamhill County vineyards and Chehalem Mountains",
  geoPosition: "45.3018;-122.9718",
  geoPlacename: "Newberg, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Newberg", zips: "97132" },
    { name: "Dundee", zips: "97115" },
    { name: "Dayton", zips: "97114" },
    { name: "McMinnville", zips: "97128" },
    { name: "Sherwood", zips: "97140" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Tualatin", zips: "97062" },
    { name: "Wilsonville", zips: "97070" },
    { name: "Canby", zips: "97013" },
    { name: "Forest Grove", zips: "97116" },
    { name: "Hillsboro", zips: "97123, 97124" },
    { name: "Beaverton", zips: "97003–97008" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "West Linn", zips: "97068" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Lafayette", zips: "97127" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Newberg home or George Fox campus in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Newberg", desc: "FedEx and UPS locations in Newberg and nearby Sherwood and Tigard offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission to reduce errors — critical for wine country travelers with tight departure dates." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no hunting for public appointments." },
  ],
  testimonials: [
    {
      name: "Sarah M.",
      location: "Newberg, OR",
      rating: 5,
      text: "Study abroad semester in Italy starting in 12 days and my passport was expired. Fast Passport Center renewed it in 5 days. I shipped from the Sherwood FedEx. George Fox University student approved!",
      service: "Passport Renewal",
    },
    {
      name: "Robert D.",
      location: "Dundee, OR",
      rating: 5,
      text: "Wine tour of Burgundy in two weeks and realized my passport only had 4 months validity. Fast Passport Center got me a renewed passport in 6 days. Yamhill County wine lovers, this service is a must.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer L.",
      location: "Newberg, OR",
      rating: 5,
      text: "Medical conference in London announced last minute. Fast Passport Center handled emergency processing and I had my passport in 3 days. The daily updates were incredible.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "newberg-post",
      name: "Newberg Post Office",
      type: "USPS Passport Acceptance",
      address: "401 E 1st St",
      city: "Newberg",
      state: "OR",
      zip: "97132",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Newberg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Newberg. Appointment required — book online in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "sherwood-post",
      name: "Sherwood Post Office",
      type: "USPS Passport Acceptance",
      address: "14895 SW Sherwood Blvd",
      city: "Sherwood",
      state: "OR",
      zip: "97140",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Sherwood — about 7 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Sherwood option for north Newberg and Chehalem Valley residents.",
      icon: "ri-mail-line",
    },
    {
      id: "mcminnville-post",
      name: "McMinnville Post Office",
      type: "USPS Passport Acceptance",
      address: "700 NE Baker St",
      city: "McMinnville",
      state: "OR",
      zip: "97128",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "McMinnville — about 16 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "McMinnville option for Yamhill County wine country travelers.",
      icon: "ri-mail-line",
    },
    {
      id: "dundee-post",
      name: "Dundee Post Office",
      type: "USPS Passport Acceptance",
      address: "1000 SW 7th St",
      city: "Dundee",
      state: "OR",
      zip: "97115",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Dundee — about 4 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Dundee option for west Newberg residents in the Red Hills wine district.",
      icon: "ri-mail-line",
    },
    {
      id: "tualatin-post",
      name: "Tualatin Post Office",
      type: "USPS Passport Acceptance",
      address: "19250 SW Martinazzi Ave",
      city: "Tualatin",
      state: "OR",
      zip: "97062",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Tualatin — about 13 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tualatin option for northeast Newberg and Portland metro commuters.",
      icon: "ri-mail-line",
    },
    {
      id: "hillsboro-post",
      name: "Hillsboro Main Post Office",
      type: "USPS Passport Acceptance",
      address: "1185 NE Grant St",
      city: "Hillsboro",
      state: "OR",
      zip: "97124",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm, Sat 9am–12pm",
      distance: "Hillsboro — about 18 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Hillsboro option for north Newberg residents in Washington County.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Newberg before international travel?",
      a: "Yes. Through Fast Passport Center, Newberg travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — ideal for wine country and George Fox University study abroad travelers.",
    },
    {
      q: "How fast can I renew my passport in Newberg?",
      a: "Fast passport renewal in Newberg is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my wine country trip to Europe?",
      a: "Most European countries require your passport to be valid for at least 3–6 months beyond your arrival date — and for wine tours to France, Italy, or Spain, that validity requirement catches many travelers off guard. Fast Passport Center can expedite your renewal before your Yamhill County wine tour departure.",
    },
    {
      q: "Can I get a same-day passport appointment near Newberg?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Newberg, Fast Passport Center is the faster and more predictable option — no three-hour Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Newberg?",
      a: "Yes. Child passport expediting in Newberg is possible through Fast Passport Center, but child passports (for minors 15 and under) require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps.",
    },
    {
      q: "Does Newberg have a passport agency?",
      a: "Newberg, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 650 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Newberg residents.",
    },
    {
      q: "How long does a standard passport take in Newberg?",
      a: "Standard USPS passport processing from Newberg runs 6–13 weeks — sometimes longer during peak travel season (May–August). If your trip is sooner, you need expedited processing. Fast Passport Center offers Newberg travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I renew my passport in Newberg without visiting an office?",
      a: "Yes. Most Newberg-area adults renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Newberg, Sherwood, or Tigard.",
    },
    {
      q: "What airports do Newberg travelers typically fly from?",
      a: "Newberg travelers typically use Portland International Airport (PDX) — about 25 miles northeast — for all domestic and international departures. PDX offers direct flights to Amsterdam, London, Reykjavik, Tokyo, and more. Fast Passport Center can help you meet your deadline for any PDX departure.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Newberg", address: "2900 Portland Rd, Newberg, OR 97132", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Newberg", address: "401 E 1st St, Newberg, OR 97132", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Sherwood", address: "14895 SW Sherwood Blvd, Sherwood, OR 97140", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Tualatin", address: "19238 SW Martinazzi Ave, Tualatin, OR 97062", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — McMinnville", address: "300 NE Highway 99W, McMinnville, OR 97128", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — McMinnville", address: "2655 NE McDonald Ln, McMinnville, OR 97128", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Sherwood", address: "16050 SW Langer Dr, Sherwood, OR 97140", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Tigard", address: "14000 SW Pacific Hwy, Tigard, OR 97224", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Hillsboro", address: "2045 NE Cornell Rd, Hillsboro, OR 97124", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walgreens — Dundee", address: "1000 SW 7th St, Dundee, OR 97115", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Newberg DMV Office",
      address: "610 E 1st St, Newberg, OR 97132",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Yamhill County DMV office in Newberg. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "McMinnville DMV Office",
      address: "300 NE Highway 99W, McMinnville, OR 97128",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "McMinnville option for south Newberg and wine country residents.",
      featured: false,
    },
    {
      name: "Sherwood DMV Office",
      address: "14895 SW Sherwood Blvd, Sherwood, OR 97140",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Sherwood option for north Newberg and Chehalem Mountain area residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 25 miles northeast",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August (wine harvest season) and December–January.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Newberg travelers use SEA for additional flight options or better pricing. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
    {
      code: "HIO",
      name: "Hillsboro Airport",
      distance: "General aviation — about 18 miles north",
      address: "3355 NE Cornell Rd, Hillsboro, OR 97124",
      note: "Hillsboro Airport is general aviation only — no commercial flights. International travelers use PDX.",
      flightUrl: "https://www.portofportland.com/hio",
      flightLabel: "HIO Airport Info",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is built for urgent last-minute travel from Newberg and the Yamhill County wine country.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Newberg residents renew fast — perfect for wine country tours, George Fox study abroad, and family travel to Europe and Canada.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS near Newberg", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Newberg travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Newberg families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Newberg residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, bypassing the public queue entirely.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Newberg, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Newberg residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Newberg, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Newberg travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Newberg families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Newberg, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Until After Harvest Season Booking", description: "Newberg wine country travelers often book France, Italy, or Spain trips in spring and don't check passport validity until departure week. Government processing runs 6–13+ weeks. Apply as soon as you book your wine tour." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos taken at local drugstores often get rejected for lighting, background, or expression issues. We provide exact specifications and catch photo problems before your application is submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our pre-submission checklist catches errors for Newberg applicants before they become delays." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what's needed based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming 6 Months Validity is Enough", description: "Many European wine country destinations require your passport to be valid for 6 months beyond your return date — not just your arrival. If you have 5 months left, you may still be denied boarding." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Between Passport and Ticket", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect — especially for international flights out of PDX." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Newberg travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Newberg", description: "Drop off at any FedEx or UPS near Newberg or Sherwood — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Newberg" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Forest Grove", route: "/cities/forest-grove" },
    { name: "Emergency passport help near Tigard", route: "/cities/tigard" },
    { name: "Rush passport service in Tualatin", route: "/cities/tualatin" },
    { name: "Fast passport renewal for Yamhill County wine country travelers", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location near Newberg — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Newberg and all of Yamhill County wine country remotely",
  serviceAreaIntro: "Whether you are in downtown Newberg, Dundee's Red Hills wine district, Dayton, Lafayette, or anywhere in Yamhill County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Newberg and Yamhill County zip codes. Anywhere in the Chehalem Valley, Dundee Hills, or surrounding wine country — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Newberg as simple as possible. Most Newberg clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Newberg and Yamhill County wine country travelers. We review your documents before submission to catch issues before they become problems.",
  testimonialsIntro: "Real reviews from Newberg, Dundee, and Yamhill County wine country travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Newberg and Yamhill County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};